import React, { useEffect, useState } from "react";
import { api, fmtINR, daysSince } from "@/lib/api";
import {useAuth} from "@/auth";

export default function RecordsPage() {
  const {user}=useAuth();
  const [tab, setTab] = useState("quotes");
  const [quotes, setQuotes] = useState([]);
  const [pcs, setPcs] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const normalize = v => String(v == null ? "" : v).toLocaleLowerCase();
  const needle = normalize(search.trim());
  const filteredQuotes = quotes.filter(q => normalize([
    q.serial_no,q.quote_number,q.customer_name,q.created_by_name,
    q.quote_date,q.created_at,q.status,
    ...(q.items || []).map(i => [i.vehicle_name,i.product_name,i.description].join(" "))
  ].join(" ")).includes(needle));
  const filteredPCs = pcs.filter(pc => normalize([
    pc.pc_number,pc.pc_number_display,pc.pc_reference,pc.customer_name,
    pc.created_by_name,pc.pc_date,pc.created_at,pc.vehicle_name,pc.vehicle_model,
    pc.chassis_last4,pc.container_type,pc.pc_type
  ].join(" ")).includes(needle));
  const matches = tab === "quotes" ? filteredQuotes : filteredPCs;

  useEffect(() => {
    api.get("/records/all/quotations").then(r => setQuotes(r.data));
    api.get("/records/all/pcs").then(r => setPcs(r.data));
  }, []);

  return (
    <div>
      <h1 className="text-2xl font-semibold text-slate-900">All Quotes & PCs</h1>
      <p className="text-sm text-slate-500 mt-1">Everyone's records across the team.</p>

      <div className="grid grid-cols-2 bg-slate-100 rounded-full p-1 mt-4">
        <button onClick={() => {setTab("quotes"); setSelected(null);}} data-testid="records-tab-quotes"
          className={`h-9 rounded-full text-sm font-medium transition-colors ${tab === "quotes" ? "bg-blue-600 text-white" : "text-slate-600"}`}>
          Quotes ({quotes.length})
        </button>
        <button onClick={() => {setTab("pcs"); setSelected(null);}} data-testid="records-tab-pcs"
          className={`h-9 rounded-full text-sm font-medium transition-colors ${tab === "pcs" ? "bg-blue-600 text-white" : "text-slate-600"}`}>
          PCs ({pcs.length})
        </button>
      </div>

      <div className="mt-4 space-y-2">
        <label htmlFor="records-search-input" className="block text-sm font-semibold text-slate-700">Search {tab === "quotes" ? "quotations" : "PCs"}</label>
        <div className="flex gap-2">
          <input id="records-search-input" data-testid="records-search-input"
            value={search} onChange={e=>{setSearch(e.target.value);setSelected(null);}}
            placeholder={tab==="quotes"?"Search number, customer, vehicle, staff or date…":"Search PC number, customer, vehicle, chassis or date…"}
            className="min-w-0 w-full h-11 rounded-xl border border-slate-300 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
          {search && <button type="button" onClick={()=>{setSearch("");setSelected(null);}} className="px-3 border rounded-xl text-sm">Clear</button>}
        </div>
        <div className="text-xs text-slate-500">{matches.length} matching {tab==="quotes"?"quotation(s)":"PC(s)"} of {tab==="quotes"?quotes.length:pcs.length} total</div>
      </div>
      <div className="space-y-3 mt-4">
        {tab === "quotes" ? (
          <>
            {filteredQuotes.map(q => (
              <div key={q.id} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-between gap-3" data-testid={`record-quote-${q.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{q.customer_name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">by {q.created_by_name} · {daysSince(q.created_at)}d ago · {q.items.length} item{q.items.length > 1 ? "s" : ""}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-semibold text-blue-600 tabular-nums">{fmtINR(q.total)}</div>
                  <div className={`text-[10px] font-semibold uppercase tracking-wide mt-0.5 ${q.status === "confirmed" ? "text-emerald-600" : "text-amber-600"}`}>{q.status}</div>
                </div>
                <button type="button" onClick={()=>setSelected(selected?.id===q.id?null:{id:q.id,kind:"quote"})}
                  className="shrink-0 rounded-lg border border-blue-200 px-3 py-2 text-xs font-semibold text-blue-700">Select</button>
              </div>
            ))}
            {filteredQuotes.length === 0 && <div className="text-center py-10 text-slate-500 text-sm">No quotations yet.</div>}
          </>
        ) : (
          <>
            {filteredPCs.map(p => (
              <div key={p.id} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-between gap-3" data-testid={`record-pc-${p.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 font-mono-tab">{p.pc_number}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{p.customer_name} · by {p.created_by_name} · {daysSince(p.created_at)}d ago</div>
                </div>
                <div className="text-right shrink-0">
                  {p.chassis_last4
                    ? <span className="chassis-tag">{p.chassis_last4}</span>
                    : <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-600">No vehicle</span>}
                </div>
                <button type="button" onClick={()=>setSelected(selected?.id===p.id?null:{id:p.id,kind:"pc"})}
                  className="shrink-0 rounded-lg border border-blue-200 px-3 py-2 text-xs font-semibold text-blue-700">Select</button>
              </div>
            ))}
            {filteredPCs.length === 0 && <div className="text-center py-10 text-slate-500 text-sm">No PCs yet.</div>}
          </>
        )}
      </div>
      {selected && (() => {
        const rec=selected.kind==="quote"?quotes.find(q=>q.id===selected.id):pcs.find(p=>p.id===selected.id);
        if(!rec)return null;
        const isQuote=selected.kind==="quote";
        const canEditQuote=(rec.status==="draft" || user?.role==="assistant") &&
          !rec.historical_source_id && (rec.items||[]).length>0 &&
          !!rec.items?.[0]?.container_id;
        return <div className="sticky bottom-20 mt-4 rounded-2xl border-2 border-blue-300 bg-blue-50 p-4 shadow-lg space-y-2">
          <div className="flex justify-between gap-3"><div className="min-w-0"><div className="text-xs uppercase font-semibold text-blue-700">Selected {isQuote?"quotation":"PC"}</div>
            <div className="font-semibold break-words">{isQuote?(rec.quote_number||rec.serial_no||"Quotation"):(rec.pc_reference||rec.pc_number||"PC")}</div>
            <div className="text-sm text-slate-600">{rec.customer_name}</div></div>
            <button type="button" onClick={()=>setSelected(null)} className="text-xs underline text-slate-600">Close</button></div>
          {isQuote
            ? canEditQuote
              ? <a href={"/quotation?edit="+encodeURIComponent(rec.id)} className="inline-flex h-10 items-center justify-center rounded-xl bg-blue-600 px-5 text-sm font-semibold text-white">Edit selected quotation</a>
              : <p className="text-xs text-amber-800">This quotation cannot be edited through the standard container quotation form. Historical imports and incomplete item snapshots remain protected.</p>
            : (user?.role==="admin" || (rec.created_by===user?.id && !!user?.permissions?.confirm_pc)) ? <a href={"/pcs/edit/"+encodeURIComponent(rec.id)} className="inline-flex h-10 items-center justify-center rounded-xl bg-blue-600 px-5 text-sm font-semibold text-white">Edit selected PC</a> : <p className="text-xs text-slate-600">View-only: another staff member’s PC.</p>}
        </div>;
      })()}
    </div>
  );
}

// fc-assistant-team-edit-records-20260922
